import { ImportedListing } from '../_types/imported-listing'
import { ListingTag } from '../_types/listing-tag'
import { QuickPassProfile } from '../_types/quickpass-profile'
import { DailyPepTalkSnapshot } from '../_types/daily-pep-talk'

const MS_IN_DAY = 24 * 60 * 60 * 1000

const formatDateString = (timestamp: number): string => {
  const date = new Date(timestamp)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const findMostRecentListing = (
  listings: ImportedListing[],
): ImportedListing | null => {
  if (listings.length === 0) {
    return null
  }
  return listings.reduce<ImportedListing>((newest, candidate) => {
    if (candidate.createdAt > newest.createdAt) {
      return candidate
    }
    return newest
  }, listings[0])
}

const countTaggedAs = (
  tags: Record<string, ListingTag>,
  target: ListingTag,
): number => {
  return Object.values(tags).filter(value => value === target).length
}

/**
 * Builds the small JSON-serialisable snapshot that feeds the daily
 * pep-talk LLM prompt. Light on data, heavy on signal — first name,
 * streak, what they tagged, whether they're close to move-in.
 *
 * @param {QuickPassProfile} profile - the renter profile
 * @param {ImportedListing[]} listings - their tracked listings
 * @param {Record<string, ListingTag>} tags - their tag map
 * @param {number} now - timestamp marking "now" (for testability)
 * @returns {DailyPepTalkSnapshot} the rolled-up snapshot
 */
const buildDailyPepTalkSnapshot = (
  profile: QuickPassProfile,
  listings: ImportedListing[],
  tags: Record<string, ListingTag>,
  now: number,
): DailyPepTalkSnapshot => {
  const recentListing = findMostRecentListing(listings)
  const yesTagged = countTaggedAs(tags, 'yes')
  const maybeTagged = countTaggedAs(tags, 'maybe')
  const noTagged = countTaggedAs(tags, 'no')
  const archivedCount = profile.archivedListingIds.length

  const moveInTimestamp = profile.unitPreferences.preferredMoveInDate
  const daysUntilMoveIn =
    moveInTimestamp === null
      ? null
      : Math.round((moveInTimestamp - now) / MS_IN_DAY)

  const daysSinceJoined = Math.max(
    0,
    Math.round((now - profile.createdAt) / MS_IN_DAY),
  )

  return {
    firstName: profile.firstName,
    dateString: formatDateString(now),
    streakDays: profile.streakState.currentStreakDays,
    longestStreakDays: profile.streakState.longestStreakDays,
    listingsTrackedCount: listings.length,
    yesTaggedCount: yesTagged,
    maybeTaggedCount: maybeTagged,
    noTaggedCount: noTagged,
    archivedCount,
    daysUntilMoveIn,
    daysSinceJoined,
    recentlyImportedTitle: recentListing?.title ?? null,
    hasMonthlyTakeHomeIncome: profile.monthlyTakeHomeIncome !== null,
    unlockedAchievementsCount: profile.unlockedAchievementIds.length,
  }
}

export { buildDailyPepTalkSnapshot, formatDateString }
